import React from 'react'
import Slider from 'react-rangeslider'
import { connect } from 'react-redux'
import Currency from 'macguffin/Utils/Currency'
import { Component } from 'macguffin'
import { uiButtonPressed, setPopupScreen, setAutoPlayReStart } from '../../../redux/actions'
import { BUTTON_CLOSE, POPUP_SCREENS } from '../../../redux/constants'
import { SET_SPINS, RUN_AUTOPLAY } from 'macguffin/components/autoplay/constants'
import { setSpins, setAutoplay, runAutoPlay, updateAutoplayBalance } from 'macguffin/components/autoplay/actions'
import config from 'config'
import SvgIcon from '../../svg/SvgIcon'
import TitleBar from '../../popups/TitleBar'
import { stopPropagation } from '../../../utils'

import 'react-rangeslider/lib/index.css'

const GB = 'GB'
const DEF_SPINS = [10, 25, 50, 100, 250, 500, 1000]
const UK_SPINS = [5, 10, 15, 20, 100]
const FORMAT_STYLE = {
  showCurrency: true,
  useSeparators: true,
  showSubUnits: true
}

/**
 * This is the part of the Autoplay component which is
 * visible in the settings.
 */
@connect(state => ({
  autoPlay: state.autoPlay,
  playing: state.base.playing,
  legalBets: state.session.legalBets,
  betAmount: state.session.betAmount,
  balance: state.session.balance,
  country: state.session.country,
  dimensions: state.UI.dimensions,
}), {
  setSpins,
  setAutoplay,
  runAutoPlay,
  uiButtonPressed,
  updateAutoplayBalance,
  setPopupScreen,
  setAutoPlayReStart,
})
export default class Menu extends Component {

  constructor(props) {
    super(props)

    /**
     * Initial state of the component.
     * @type {advancedMenuVisible: bool}
     */
    this.state = {
      advancedMenuVisible: false,
    }
  }

  componentDidMount() {
    const {
      setAutoplay,
      autoPlay: { lossLimit, winLimit, singleWinLimit },
    } = this.props
    setAutoplay({
      lossLimit: Math.min(+lossLimit, this.getLossLimitMax()),
      winLimit: Math.min(+winLimit, this.getMax()),
      singleWinLimit: Math.min(+singleWinLimit, this.getMax()),
      spins: 0,
    })
    if (lossLimit || winLimit || singleWinLimit) {
      this.setState({ advancedMenuVisible: true })
    }
  }

  /**
   * Get a new max limit, based on the current bet-amount.
   * @returns {number}
   */
  getMax = () => {
    const { betAmount } = this.props
    return betAmount * 1000
  }

  /**
   * Get a new loss limit, based on the balance or the current bet-amount.
   * @returns {number}
   */
  getLossLimitMax = () => {
    const { betAmount, balance } = this.props
    return Math.min(balance, betAmount * 5000)
  }

  /**
   * Format the limit to the user's currency.
   * @param {number} value - Current limit
   * @returns {string}
   */
  getLimit = (value) => {
    if (!value || value <= 0.01)
      return this.t('settings.autoplay.noLimit')

    return `${Currency.format(value, FORMAT_STYLE)}`
  }

  /**
   * Get apropriate "step" for the current value of slider.
   * @param {number} currentValue - The current value of the slider
   */
  getStep = (currentValue) => {
    const { betAmount } = this.props
    const step = currentValue ? Math.pow(10, Math.floor(currentValue).toString().length - 1) * 0.1 : betAmount
    return Math.min(step, 10000)
  }

  /**
   * Update the current state values of a limit.
   * @param {string} key - Key of the limit that needs to be updated.
   * @param {number} value - The new limit.
   */
  updateValues = (key, value) => {
    const { autoPlay, setAutoplay } = this.props
    const updatingValues = { ...autoPlay }
    updatingValues[key] = value
    const { lossLimit, winLimit, singleWinLimit, spinsLeft } = updatingValues
    setAutoplay({ lossLimit, winLimit, singleWinLimit, spins: spinsLeft })
  }

  /**
   * Set the losslimit - Forced to be bigger than the current bet value.
   * @param value
   */
  updateLossLimit = (value) => {
    const { betAmount, setAutoplay, autoPlay } = this.props
    const lossLimit = (value > 0 && betAmount >= value) ? betAmount : value
    const { winLimit, singleWinLimit, spinsLeft } = autoPlay
    setAutoplay({ lossLimit, winLimit, singleWinLimit, spins: spinsLeft })
  }

  /**
   * Returns the "correct" amount of spins for jurisdiction or country
   */
  getSpins() {
    const { dimensions: { desktopUI }, country } = this.props
    let spins
    if (config.autoplayRounds && config.autoplayRounds.length) {
      spins = config.autoplayRounds
    } else {
      spins = country !== GB ? DEF_SPINS : UK_SPINS
    }
    spins = [...spins]

    if (config.autoplayInfinite) {
      spins.push(Infinity)
    }

    if (!desktopUI && !spins.find(s => s === 0)) {
      spins.unshift(0)
    }

    return spins
  }

  toggleAdvancedMenuVisibility = () => {
    const { AUTOPLAY_ADVANCED_OPEN, AUTOPLAY_ADVANCED_CLOSE } = POPUP_SCREENS
    const { uiButtonPressed } = this.props
    const { advancedMenuVisible } = this.state

    this.setState(state => ({ advancedMenuVisible: !state.advancedMenuVisible }))
    uiButtonPressed(advancedMenuVisible ? AUTOPLAY_ADVANCED_CLOSE : AUTOPLAY_ADVANCED_OPEN)
  }

  /**
   * Starts autoplay
   * @param {number} value - Number of spins.
   * @returns {function(): void}
   */
  startAutoplay = spins => () => {
    const { uiButtonPressed } = this.props
    uiButtonPressed(SET_SPINS)
    if (spins) {
      const {
        autoPlay,
        uiButtonPressed,
        updateAutoplayBalance,
        runAutoPlay,
        setAutoplay,
        setAutoPlayReStart,
      } = this.props
      const { lossLimit, winLimit, singleWinLimit } = autoPlay

      if (config.autoplayForceAdvanced && !lossLimit) {
        return
      }

      setAutoplay({ spins, lossLimit, winLimit, singleWinLimit })
      updateAutoplayBalance()
      runAutoPlay()
      setAutoPlayReStart(true)
      uiButtonPressed(RUN_AUTOPLAY)
    }
  }

  closeAutplay = ({ resetSpisLeft }) => {
    const {
      dimensions: { desktopUI },
      uiButtonPressed,
      setPopupScreen,
      setSpins,
    } = this.props
    if (!desktopUI) {
      setPopupScreen(null)
      if (resetSpisLeft) {
        setSpins(0)
      }
      uiButtonPressed(BUTTON_CLOSE)
    }
  }

  renderValueButtonsDesktop = () => {
    const { autoPlay } = this.props
    const { spinsLeft, disabled } = autoPlay
    const spinValues = this.getSpins()
    return spinValues.map(v =>
      <button
        key={v}
        onClick={this.startAutoplay(v)}
        type="button"
        className={`${v === spinsLeft ? 'selected' : ''} ${v === Infinity ? 'infinity' : ''}`}
        disabled={disabled}
      >
        {v === Infinity ? <div>{'\u221E'}</div> : v}
      </button>
    ).reverse()
  }

  renderValueButtonsMobile = () => {
    const { autoPlay, svgIcons, setSpins, uiButtonPressed } = this.props
    const { spinsLeft } = autoPlay
    const { AUTOPLAY_DECREASE_SPINS, AUTOPLAY_INCREASE_SPINS } = POPUP_SCREENS
    const spinValues = this.getSpins()

    const decreaseEnabled = spinsLeft > spinValues[0]
    const increaseEnabled = spinsLeft < spinValues[spinValues.length - 1]

    const onDecrease = enabled => {
      if (enabled) {
        const { spinsLeft } = this.props.autoPlay
        const currentIndex = spinValues.findIndex(spinValue => spinValue === spinsLeft)
        const decreasedIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : 0
        setSpins(spinValues[decreasedIndex])
        uiButtonPressed(AUTOPLAY_DECREASE_SPINS)
      }
    }

    const onIncrease = enabled => {
      if (enabled) {
        const { spinsLeft } = this.props.autoPlay
        const currentIndex = spinValues.findIndex(spinValue => spinValue === spinsLeft)
        const increasedIndex = currentIndex + 1 < spinValues.length ? currentIndex + 1 : spinValues.length - 1
        setSpins(spinValues[increasedIndex])
        uiButtonPressed(AUTOPLAY_INCREASE_SPINS)
      }
    }

    return (
      <React.Fragment>
        <div className="spin-number-title">{this.t('settings.autoplay.numberOfSpins')}</div>
        <div className="spin-number-wrapper">
          <div className="spin-number-content">
            <SvgIcon
              svgIcons={svgIcons}
              additionalClass="autospin-decrease"
              disabled={!decreaseEnabled}
              iconName="minus"
              hasClickIcon={true}
              onClick={() => onDecrease(decreaseEnabled)}
              holdButtonEnabled={true}
            />
            <div className={`value ${spinsLeft === Infinity ? 'infinity' : ''}`} >{spinsLeft === Infinity ? '\u221E' : spinsLeft}</div>
            <SvgIcon
              svgIcons={svgIcons}
              additionalClass="autospin-increase"
              disabled={!increaseEnabled}
              iconName="plus"
              hasClickIcon={true}
              onClick={() => onIncrease(increaseEnabled)}
              holdButtonEnabled={true}
            />
          </div>
        </div>
      </React.Fragment>
    )
  }

  renderAdvancedMenuButton = side => (
    <div className="show-advanced">
      <div className="arrow-container" onClick={this.toggleAdvancedMenuVisibility}>
        <div className={`arrow ${side}`} />
      </div>
    </div>
  )

  render() {
    const { state, props } = this
    const { autoPlay, dimensions, svgIcons, smartLandscapeWidthCoef } = props
    const { advancedMenuVisible } = state
    const { lossLimit, winLimit, singleWinLimit } = autoPlay
    const { desktopUI, portrait, width, availableWidth } = dimensions

    const commonSlider = {
      tooltip: false,
      className: "settings-slider",
      orientation: "horizontal",
    }

    const contentStyle = !desktopUI && !portrait ? { paddingRight: width - availableWidth } : {}
    const autoPlayMenuStyle = !desktopUI && !portrait ? { width: availableWidth * smartLandscapeWidthCoef } : {}

    return (
      <div className="auto-play-wrapper" onClick={() => this.closeAutplay({ resetSpisLeft: false })}>
        {!desktopUI &&
          <div className={`title-wrapper${advancedMenuVisible ? '' : ' advance-disabled'}`}>
            <TitleBar
              titleBarOnClick={stopPropagation}
              titleBarStyle={{ width: !portrait ? availableWidth * smartLandscapeWidthCoef : '100%' }}
              titleText={this.t('settings.autoplay.head')}
              svgIcons={svgIcons}
              closeIconAdditionalClass='autoplay-close'
              onCloseIconClick={() => this.closeAutplay({ resetSpisLeft: true })}
            />
            {!portrait && advancedMenuVisible &&
              <TitleBar
                titleBarOnClick={stopPropagation}
                titleBarStyle={{ width: availableWidth * (1 - smartLandscapeWidthCoef) }}
                titleText={this.t('settings.autoplay.advanced')}
                enableCloseIcon={false}
              />
            }
          </div>
        }
        <div
          className={`content-area${advancedMenuVisible ? '' : ' advance-disabled'}`}
          onClick={stopPropagation}
          style={contentStyle}
        >
          <div
            className={`autoPlayMenu${advancedMenuVisible ? '' : ' advance-disabled'}`}
            style={{ ...autoPlayMenuStyle }}
          >
            <div className="spinNumberValues">
              {desktopUI ? this.renderValueButtonsDesktop() : this.renderValueButtonsMobile()}
            </div>
            {(!advancedMenuVisible || !desktopUI) && this.renderAdvancedMenuButton(advancedMenuVisible ? 'left' : 'right')}
          </div>
          <div className={`advance-area-wrapper ${advancedMenuVisible ? 'enabled' : ''}`}>
            <div className="advance-area">
              <h4>
                {`${this.t('settings.autoplay.advanced')} settings`.toUpperCase()}
              </h4>
              <div className="sub-divider centralized" />
              <div className="advanced-slider">
                <div className="label">
                  <span>{this.t('settings.autoplay.lossLimit')}: </span><span className="value">{this.getLimit(lossLimit)}</span>
                </div>
                <Slider {...commonSlider} value={lossLimit} max={this.getLossLimitMax()}
                  onChange={this.updateLossLimit} step={this.getStep(lossLimit)} />
              </div>
              <div className="advanced-slider">
                <div className="label">
                  <span>{this.t('settings.autoplay.winLimit')}: </span><span className="value">{this.getLimit(winLimit)}</span>
                </div>
                <Slider {...commonSlider} value={winLimit} max={this.getMax()}
                  onChange={e => { this.updateValues('winLimit', e) }} step={this.getStep(winLimit)} />
              </div>
              <div className="advanced-slider">
                <div className="label">
                  <span>{this.t('settings.autoplay.singleWinLimit')}: </span><span className="value">{this.getLimit(singleWinLimit)}</span>
                </div>
                <Slider {...commonSlider} value={singleWinLimit} max={this.getMax()}
                  onChange={e => { this.updateValues('singleWinLimit', e) }}
                  step={this.getStep(singleWinLimit)} />
              </div>
            </div>
            {advancedMenuVisible && desktopUI && this.renderAdvancedMenuButton('left')}
          </div>
        </div>
      </div>
    )
  }
}